import { useState } from "react";
import {
    Box,
    CircularProgress,
    Button
} from "@mui/material";
import { useNavigate, useParams } from 'react-router-dom';
import axios from "../api/axios";
import useAxiosFunction from "../hooks/useAxiosFunction";
import useJobAdvert from "../hooks/useJobAdvert";
import usePreventUnload from "../hooks/usePreventUnload";
import HeroBanner from "../components/HeroBanner";
import PersonalDetailsForm from "../components/application/PersonalDetailsForm";
import EligibilityForm from "../components/application/EligibilityForm";
import DocumentsForm from "../components/application/DocumentsForm";
import ReviewForm from "../components/application/ReviewForm";
import ApplicationProgress from "../components/application/ApplicationProgress";

import styles from "../styles/jobAdvert.module.scss";
import { Turnstile } from "@marsidev/react-turnstile";

export default function JobApplication() {

    const { id } = useParams();
    const navigate = useNavigate();
    const goBack = () => navigate(-1);
    const [turnstileToken, setTurnstileToken] = useState(null);
    const [turnstileError, setTurnstileError] = useState("");
    var email_regex = /^[a-zA-Z0-9._%+-]{1,64}@(?:[a-zA-Z0-9-]{1,63}\.){1,125}[a-zA-Z]{2,63}$/;
    var pc_regex = /^[a-zA-Z]{1,2}[0-9][0-9a-zA-Z]?\s?[0-9][a-zA-Z]{2}$/;
    var eir_regex = /^([AC-FHKNPRTV-Y]{1}[0-9]{2}|D6W)[ ]?[0-9AC-FHKNPRTV-Y]{4}$/;
    var phone_number_regex =/^\+?([0-9]\s*){11,17}$/;
    const [submitting, setSubmitting] = useState(false);

    const {
        jobAdvert,
        loading: advertLoading
    } = useJobAdvert(id);

    const [response, error, loading, axiosFetch] = useAxiosFunction();
    usePreventUnload(loading);
    usePreventUnload(submitting);
    const [currentStep, setCurrentStep] = useState(1);

    const [application, setApplication] = useState({

        jobAdvertId: Number(id),

        personalDetails: {

            title: "",

            firstName: "",

            lastName: "",

            addressLine1: "",

            addressLine2: "",

            townCity: "",

            county: "",

            postcode: "",

            mobile: "",

            altContactNumber: "",

            emailAddress: "",

            confirmEmail: ""

        },

        eligibility: {

            eligibilityStatus: "",

            eligibilityStatusOther: "",

            salaryRange: "",

            hasCscsCard: "",

            cscsCardType: "",

            hasDrivingLicence: "",

            endorsements: "",

            endorsementsDetails: ""

        },

        documents: {

            cv: null,

            coverLetter: null

        }

    });

    const [errors, setErrors] = useState({});

    const handlePersonalChange = (event) => {

        const { name, value } = event.target;

        setApplication(prev => ({

            ...prev,

            personalDetails: {

                ...prev.personalDetails,

                [name]: value

            }

        }));

    };

    const handleEligibilityChange = (event) => {

        const { name, value } = event.target;

        setApplication(prev => ({

            ...prev,

            eligibility: {

                ...prev.eligibility,

                [name]: value

            }

        }));

    };

    const handleDocumentUpload = (name, file) => {

        setApplication(prev => ({

            ...prev,

            documents: {

                ...prev.documents,

                [name]: file

            }

        }));

    };

    const validatePersonalDetails = () => {

        const validationErrors = {};

        if (!application.personalDetails.title.trim()) {
            validationErrors.title = "Please select a title.";
        }

        if (!application.personalDetails.firstName.trim()) {
            validationErrors.firstName = "First Name is required.";
        }

        if (!application.personalDetails.lastName.trim()) {
            validationErrors.lastName = "Last Name is required.";
        }

        if (!application.personalDetails.addressLine1.trim()) {
            validationErrors.addressLine1 = "Address Line 1 is required.";
        }

        if (!application.personalDetails.townCity.trim()) {
            validationErrors.townCity = "Town / City is required.";
        }

        if (!application.personalDetails.county.trim()) {
            validationErrors.county = "County is required.";
        }

        if (!application.personalDetails.postcode.trim()) {
            validationErrors.postcode = "Postcode is required.";
        }
        else if ((!pc_regex.test(application.personalDetails.postcode.trim()) && !eir_regex.test(application.personalDetails.postcode.trim()))) {
            validationErrors.postcode = "Please enter a valid postcode.";
        }

        if (!application.personalDetails.mobile.trim()) {
            validationErrors.mobile = "Mobile Number is required.";
        }
        if(application.personalDetails.mobile.length > 0 && !phone_number_regex.test(application.personalDetails.mobile)){
            validationErrors.mobile = "Please provide a valid contact phone number.";
        }
        if(application.personalDetails.altContactNumber.length > 0 && !phone_number_regex.test(application.personalDetails.altContactNumber)){
            validationErrors.altContactNumber = "Please provide a valid contact phone number.";
        }

        if (!application.personalDetails.emailAddress.trim()) {
            validationErrors.emailAddress = "Email Address is required.";
        }

        if (!application.personalDetails.confirmEmail.trim()) {
            validationErrors.confirmEmail = "Please confirm your email.";
        }
        if (application.personalDetails.emailAddress.trim() != "" && !email_regex.test(application.personalDetails.emailAddress.trim())) {
            validationErrors.emailAddress = "Please enter a valid email.";
        }

        if (application.personalDetails.confirmEmail.trim()!= "" && !email_regex.test(application.personalDetails.confirmEmail.trim())) {
            validationErrors.confirmEmail = "Please enter a valid email.";
        }

        if (
            application.personalDetails.emailAddress &&
            application.personalDetails.confirmEmail &&
            application.personalDetails.emailAddress !== application.personalDetails.confirmEmail
        ) {
            validationErrors.confirmEmail = "Email addresses do not match.";
        }

        return validationErrors;

    };

    const validateEligibility = () => {

        const errors = {};

        if (!application.eligibility.eligibilityStatus) {
            errors.eligibilityStatus = "Please select your eligibility status";
        }

        if (
            application.eligibility.eligibilityStatus === "Other" &&
            !application.eligibility.eligibilityStatusOther?.trim()
        ) {
            errors.eligibilityStatusOther = "Please specify your status";
        }
        if(!application.eligibility.salaryRange){
            errors.salaryRange = "Please enter your required salary range";
        }
        console.log(
            application.eligibility.hasDrivingLicence,
            typeof application.eligibility.hasDrivingLicence
        );
        if(application.eligibility.hasDrivingLicence == null || application.eligibility.hasDrivingLicence == ""){
            errors.hasDrivingLicence = "Please select an option";
        }

        if(application.eligibility.hasCscsCard == null || application.eligibility.hasCscsCard == ""){
            errors.hasCscsCard = "Please select an option";
        }

        if (application.eligibility.hasCscsCard === "true" && !application.eligibility.cscsCardType?.trim()) {
            errors.cscsCardType = "Please enter CSCS card type";
        }
        if(application.eligibility.hasDrivingLicence != null && application.eligibility.hasDrivingLicence == "true"){
            if(application.eligibility.endorsements == null || application.eligibility.endorsements == ""){
                errors.endorsements = "Please select an option";
            }

            if (application.eligibility.endorsements === "true" && !application.eligibility.endorsementsDetails?.trim()) {
                errors.endorsementsDetails = "Please provide endorsement details";
            }
        }

        return errors;
    };

    const validateDocuments = () => {

        const errors = {};

        if (!application.documents.cv) {

            errors.cv = "Please upload your CV.";

        }

        return errors;

    };

    const validateReview = () => {

        const errors = {};

        if (!application.documents.cv) {
            errors.cv = "CV is required";
        }

        return errors;

    };

    const handleContinue = () => {

        const stepErrors = validateStep(currentStep);

        setErrors(stepErrors);

        if (Object.keys(stepErrors).length > 0) {
            return;
        }

        setCurrentStep(prev => prev + 1);

    };

    const validateStep = (step) => {
        switch (step) {

            case 1:
                return validatePersonalDetails();

            case 2:
                return validateEligibility();

            case 3:
                return validateDocuments();

            case 4:
                return validateReview();

            default:
                return {};

        }
    };

    const handleSubmitApplication = async () => {

        if (!turnstileToken) {
            setTurnstileError("Please complete the verification.");
            return;
        }

        if (submitting) {
            return;
        }

        setSubmitting(true);

        try {

            const payload = {
                jobAdvertId: application.jobAdvertId,
                personalDetails: application.personalDetails,
                eligibility: application.eligibility,
                turnstileToken
            };

            const res = await axiosFetch({
                axiosInstance: axios,
                method: "post",
                url: "/recordJobApplication",
                requestConfig: {
                    data: payload
                }
            });

            // IMPORTANT: adjust depending on axiosFetch shape
            const jobApplicationId = res?.id || res?.data?.id;

            if (!jobApplicationId) {
                throw new Error("Job Application ID not returned");
            }

            // Upload CV if exists
            if (application.documents.cv) {

                const formData = new FormData();
                formData.append("file", application.documents.cv);

                await axios.post(
                    `/job-application/${jobApplicationId}/upload-cv`,
                    formData,
                    {
                        headers: {
                            "Content-Type": "multipart/form-data"
                        }
                    }
                );
            }

            navigate("/application-success", {
                state: {
                    applicationId: jobApplicationId
                }
            });

        } catch (err) {
            console.error("Submission failed:", err);
        } finally {
            setSubmitting(false);
        }
    };



    if (advertLoading || !jobAdvert) {

        return (

            <Box
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    minHeight: "60vh"
                }}
            >

                <CircularProgress />

            </Box>

        );

    }

    return (

        <div className={styles.page}>

            <HeroBanner
                title={jobAdvert.jobTitle}
                subtitle="Apply for this position"
            />
            <ApplicationProgress currentStep={currentStep} />

            {currentStep === 1 && (
                <PersonalDetailsForm
                    data={application.personalDetails}
                    errors={errors}
                    handleChange={handlePersonalChange}
                    onContinue={handleContinue}
                />

            )}

            {currentStep === 2 && (

                <EligibilityForm
                    data={application.eligibility}
                    errors={errors}
                    handleChange={handleEligibilityChange}
                    onBack={() => setCurrentStep(1)}
                    onContinue={handleContinue}
                />

            )}
            {currentStep === 3 && (

                <DocumentsForm

                    data={application.documents}

                    errors={errors}

                    handleDocumentUpload={handleDocumentUpload}

                    onBack={() => setCurrentStep(2)}

                    onContinue={handleContinue}

                />

            )}
            {currentStep === 4 && (

                <ReviewForm
                    data={application}
                    jobAdvert={jobAdvert}
                    loading={submitting}
                    onBack={() => setCurrentStep(3)}
                    onSubmit={handleSubmitApplication}
                    turnstileToken={turnstileToken}
                    setTurnstileToken={setTurnstileToken}
                    turnstileError={turnstileError}
                    setTurnstileError={setTurnstileError}
                />

            )}
            
        </div>
        

    );

}